<template>
    <div class="rtp-exercise-library">
        <!-- Header -->
        <div class="library-header">
            <div class="header-top">
                <h4>
                    <i class="bi bi-list-check"></i>
                    {{ assignmentMode ? 'Assign Exercises' : 'Exercise Library' }}
                </h4>
                <div v-if="canManageExercises && !assignmentMode" class="admin-controls">
                    <button class="btn btn-sm" :class="adminMode ? 'btn-secondary' : 'btn-outline-secondary'"
                        @click="adminMode = !adminMode">
                        <i class="bi bi-gear"></i>
                        {{ adminMode ? 'Exit Admin' : 'Admin Mode' }}
                    </button>
                    <button v-if="adminMode" class="btn btn-sm btn-primary" @click="openAddExerciseModal()">
                        <i class="bi bi-plus-lg"></i>
                        Add Exercise
                    </button>
                </div>
            </div>
            <p v-if="currentPhase && !adminMode && !assignmentMode">
                Showing exercises for Phase {{ currentPhase }} ({{
                    getPhaseInfo(currentPhase).name
                }})
            </p>
            <p v-if="adminMode" class="admin-mode-notice">
                <i class="bi bi-info-circle"></i>
                Admin mode: You can add, edit, duplicate, and delete exercises
            </p>
        </div>

        <!-- Assignment Mode Banner -->
        <div v-if="assignmentMode" class="assignment-banner">
            <div class="banner-content">
                <div class="banner-icon">
                    <i class="bi bi-person-plus"></i>
                </div>
                <div class="banner-text">
                    <strong>Assigning exercises to {{ playerName }}</strong>
                    <p>Click the <i class="bi bi-plus-circle"></i> button to assign an exercise or <i
                            class="bi bi-dash-circle"></i> to remove.</p>
                </div>
            </div>
            <div class="assignment-stats">
                <span class="stat-badge">
                    <i class="bi bi-check-circle-fill"></i>
                    {{ assignedExercises.length }} assigned
                </span>
            </div>
        </div>

        <!-- Maturity Warning -->
        <div v-if="maturityCategory === 'Circa-PHV' && !adminMode" class="maturity-alert">
            <i class="bi bi-exclamation-triangle"></i>
            <div>
                <strong>Circa-PHV Considerations</strong>
                <p>
                    This player is in a rapid growth phase. Exercises should be modified
                    to reduce load intensity. Monitor for growth-related discomfort and
                    adjust accordingly.
                </p>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
            <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            <span>Loading exercises...</span>
        </div>

        <!-- Phase Navigation -->
        <div v-else class="phase-nav">
            <button v-for="phase in 5" :key="phase" :class="['phase-btn', { active: selectedPhase === phase }]" :style="selectedPhase === phase
                ? {
                    backgroundColor: getPhaseInfo(phase).color,
                    borderColor: getPhaseInfo(phase).color,
                }
                : {}
                " @click="selectedPhase = phase">
                P{{ phase }}
            </button>
        </div>

        <!-- Exercise Categories -->
        <div v-if="!loading" class="exercise-categories">
            <div v-for="category in exerciseCategories" :key="category.key" class="category-section">
                <div class="category-header" @click="toggleCategory(category.key)">
                    <div class="category-title">
                        <i :class="category.icon"></i>
                        <h5>{{ category.name }}</h5>
                    </div>
                    <span class="exercise-count">{{ getExerciseCount(category.key) }} exercises</span>
                    <i :class="expandedCategories.includes(category.key)
                        ? 'bi bi-chevron-up'
                        : 'bi bi-chevron-down'
                        "></i>
                </div>

                <transition name="slide">
                    <div v-if="expandedCategories.includes(category.key)" class="category-content">
                        <div v-if="getCategoryExercises(category.key).length === 0" class="no-exercises">
                            <i class="bi bi-info-circle"></i>
                            <span>No {{ category.name.toLowerCase() }} exercises for this
                                phase</span>
                        </div>

                        <div v-else class="exercise-list">
                            <div v-for="exercise in getCategoryExercises(category.key)" :key="exercise.id"
                                class="exercise-card" :class="{
                                    'inactive': !exercise.is_active && adminMode,
                                    'assigned': assignmentMode && isExerciseAssigned(exercise.id)
                                }">
                                <div class="exercise-header">
                                    <div class="exercise-info">
                                        <span class="exercise-name">{{
                                            exercise.exercise_name
                                        }}</span>
                                        <span v-if="exercise.progression_level" class="progression-badge" :class="'level-' + (exercise.progression_level || '').toLowerCase()
                                            ">
                                            {{ exercise.progression_level }}
                                        </span>
                                        <span v-if="exercise.maturity_category" class="maturity-badge">
                                            {{ exercise.maturity_category }}
                                        </span>
                                        <span v-if="!exercise.is_active && adminMode" class="inactive-badge">
                                            Inactive
                                        </span>
                                    </div>
                                    <div class="exercise-actions">
                                        <!-- Assignment mode action -->
                                        <button v-if="assignmentMode" class="btn-assign"
                                            :class="{ 'assigned': isExerciseAssigned(exercise.id), 'loading': assigningExercise === exercise.id }"
                                            :title="isExerciseAssigned(exercise.id) ? 'Remove from player' : 'Assign to player'"
                                            :disabled="assigningExercise === exercise.id"
                                            @click.stop="toggleExerciseAssignment(exercise)">
                                            <span v-if="assigningExercise === exercise.id"
                                                class="spinner-border spinner-border-sm"></span>
                                            <i v-else
                                                :class="isExerciseAssigned(exercise.id) ? 'bi bi-dash-circle-fill' : 'bi bi-plus-circle-fill'"></i>
                                        </button>
                                        <!-- Admin actions -->
                                        <div v-if="adminMode && !assignmentMode" class="admin-actions">
                                            <button class="btn-action btn-edit" title="Edit"
                                                @click.stop="openEditExerciseModal(exercise)">
                                                <i class="bi bi-pencil"></i>
                                            </button>
                                            <button class="btn-action btn-duplicate" title="Duplicate"
                                                @click.stop="openDuplicateModal(exercise)">
                                                <i class="bi bi-copy"></i>
                                            </button>
                                            <button class="btn-action"
                                                :class="exercise.is_active ? 'btn-deactivate' : 'btn-activate'"
                                                :title="exercise.is_active ? 'Deactivate' : 'Activate'"
                                                @click.stop="toggleExerciseActive(exercise)">
                                                <i :class="exercise.is_active ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                                            </button>
                                            <button class="btn-action btn-delete" title="Delete"
                                                @click.stop="confirmDeleteExercise(exercise)">
                                                <i class="bi bi-trash"></i>
                                            </button>
                                        </div>
                                        <button class="btn-expand" @click.stop="toggleExercise(exercise.id)">
                                            <i :class="expandedExercises.includes(exercise.id)
                                                ? 'bi bi-dash'
                                                : 'bi bi-plus'
                                                "></i>
                                        </button>
                                    </div>
                                </div>

                                <transition name="fade">
                                    <div v-if="expandedExercises.includes(exercise.id)" class="exercise-details">
                                        <div v-if="exercise.description" class="detail-section">
                                            <strong>Description</strong>
                                            <p>{{ exercise.description }}</p>
                                        </div>

                                        <div class="detail-grid">
                                            <div v-if="exercise.sets_reps" class="detail-item">
                                                <i class="bi bi-arrow-repeat"></i>
                                                <div>
                                                    <span class="detail-label">Sets/Reps</span>
                                                    <span class="detail-value">{{
                                                        exercise.sets_reps
                                                    }}</span>
                                                </div>
                                            </div>

                                            <div v-if="exercise.duration" class="detail-item">
                                                <i class="bi bi-clock"></i>
                                                <div>
                                                    <span class="detail-label">Duration</span>
                                                    <span class="detail-value">{{
                                                        exercise.duration
                                                    }}</span>
                                                </div>
                                            </div>

                                            <div v-if="exercise.intensity" class="detail-item">
                                                <i class="bi bi-speedometer2"></i>
                                                <div>
                                                    <span class="detail-label">Intensity</span>
                                                    <span class="detail-value">{{
                                                        exercise.intensity
                                                    }}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div v-if="
                                            exercise.precautions || maturityCategory === 'Circa-PHV'
                                        " class="precautions-section">
                                            <strong><i class="bi bi-shield-exclamation"></i>
                                                Precautions</strong>
                                            <p v-if="exercise.precautions">
                                                {{ exercise.precautions }}
                                            </p>
                                            <p v-if="maturityCategory === 'Circa-PHV'" class="circa-precaution">
                                                <strong>Circa-PHV:</strong> Consider 75-80% of
                                                prescribed intensity. Monitor for apophysitis symptoms.
                                            </p>
                                        </div>

                                        <!-- Tactical Diagrams -->
                                        <div class="tactical-diagrams-section">
                                            <div class="td-header">
                                                <strong><i class="bi bi-easel2"></i> Tactical Diagrams</strong>
                                                <button v-if="adminMode" class="btn btn-outline-primary btn-sm" @click.stop="openTacticalBoardPicker(exercise.id)">
                                                    <i class="bi bi-plus-lg"></i> Add
                                                </button>
                                            </div>
                                            <div v-if="getExerciseBoards(exercise.id).length > 0" class="td-boards">
                                                <div v-for="(board, bIdx) in getExerciseBoards(exercise.id)" :key="board.id" class="td-board-item">
                                                    <TacticalBoardCanvas
                                                        :title="board.title"
                                                        :notes="board.notes || ''"
                                                        :savedElements="board.elements || []"
                                                        :background="board.background || 'full-pitch'"
                                                        :savedBackgroundImage="board.background_image || ''"
                                                        :orientation="board.orientation || 'portrait'"
                                                        :imageTransform="board.image_transform || { x: 0, y: 0, scale: 1 }"
                                                        :editMode="false"
                                                    />
                                                    <button v-if="adminMode" class="btn btn-sm btn-outline-danger td-remove-btn" @click.stop="unlinkTacticalBoardFromExercise(exercise.id, board.id, bIdx)">
                                                        <i class="bi bi-x-lg"></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <p v-else class="td-empty">No tactical diagrams attached.</p>
                                        </div>

                                        <div v-if="exercise.video_url" class="video-link">
                                            <a :href="exercise.video_url" target="_blank"
                                                class="btn btn-outline-primary btn-sm">
                                                <i class="bi bi-play-circle"></i> Watch Video
                                            </a>
                                        </div>
                                    </div>
                                </transition>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>
        </div>

        <!-- Phase Summary -->
        <div v-if="!loading && phaseExercises.length > 0" class="phase-summary">
            <h5>Phase {{ selectedPhase }} Overview</h5>
            <div class="summary-content">
                <div class="summary-item">
                    <i class="bi bi-clock-history"></i>
                    <span><strong>{{
                        getPhaseInfo(selectedPhase).minDuration
                            }}</strong></span>
                </div>
                <div class="summary-item">
                    <i class="bi bi-person-check"></i>
                    <span>Lead:
                        <strong>{{ getPhaseInfo(selectedPhase).leadRole }}</strong></span>
                </div>
                <div class="summary-item">
                    <i class="bi bi-target"></i>
                    <span>{{ getPhaseInfo(selectedPhase).objective }}</span>
                </div>
            </div>
            <div class="criteria-section">
                <strong>Progression Criteria:</strong>
                <ul>
                    <li v-for="(criterion, idx) in getPhaseInfo(selectedPhase)
                        .progressionCriteria" :key="idx">
                        {{ criterion }}
                    </li>
                </ul>
            </div>
        </div>

        <!-- Add/Edit Exercise Modal -->
        <b-modal v-model="showExerciseModal" :title="editingExercise ? 'Edit Exercise' : 'Add New Exercise'" size="lg"
            scrollable hide-footer>
            <form @submit.prevent="saveExercise">
                <div class="row">
                    <div class="col-md-6 mb-3">
                        <label class="form-label">Exercise Name <span class="text-danger">*</span></label>
                        <input v-model="exerciseForm.exercise_name" type="text" class="form-control" required>
                    </div>
                    <div class="col-md-3 mb-3">
                        <label class="form-label">Phase <span class="text-danger">*</span></label>
                        <select v-model="exerciseForm.phase_number" class="form-select" required>
                            <option v-for="phase in 5" :key="phase" :value="phase">
                                Phase {{ phase }} - {{ getPhaseInfo(phase).name }}
                            </option>
                        </select>
                    </div>
                    <div class="col-md-3 mb-3">
                        <label class="form-label">Category <span class="text-danger">*</span></label>
                        <select v-model="exerciseForm.category" class="form-select" required>
                            <option v-for="cat in exerciseCategories" :key="cat.key" :value="cat.key">
                                {{ cat.name }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Description</label>
                    <textarea v-model="exerciseForm.description" class="form-control" rows="3"></textarea>
                </div>

                <div class="row">
                    <div class="col-md-4 mb-3">
                        <label class="form-label">Sets/Reps</label>
                        <input v-model="exerciseForm.sets_reps" type="text" class="form-control"
                            placeholder="e.g., 3 x 10-12 reps">
                    </div>
                    <div class="col-md-4 mb-3">
                        <label class="form-label">Duration</label>
                        <input v-model="exerciseForm.duration" type="text" class="form-control"
                            placeholder="e.g., 15-20 mins">
                    </div>
                    <div class="col-md-4 mb-3">
                        <label class="form-label">Intensity</label>
                        <input v-model="exerciseForm.intensity" type="text" class="form-control"
                            placeholder="e.g., 50-60% HR max">
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-4 mb-3">
                        <label class="form-label">Difficulty</label>
                        <select v-model="exerciseForm.difficulty" class="form-select">
                            <option value="easy">Easy</option>
                            <option value="intermediate">Intermediate</option>
                            <option value="advanced">Advanced</option>
                        </select>
                    </div>
                    <div class="col-md-4 mb-3">
                        <label class="form-label">Maturity Category</label>
                        <select v-model="exerciseForm.maturity_category" class="form-select">
                            <option :value="null">All Players</option>
                            <option value="Pre-PHV">Pre-PHV</option>
                            <option value="Circa-PHV">Circa-PHV</option>
                            <option value="Post-PHV">Post-PHV</option>
                        </select>
                    </div>
                    <div class="col-md-4 mb-3">
                        <label class="form-label">Sort Order</label>
                        <input v-model.number="exerciseForm.sort_order" type="number" class="form-control" min="0">
                    </div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Precautions / Safety Notes</label>
                    <textarea v-model="exerciseForm.precautions" class="form-control" rows="2"
                        placeholder="Any safety warnings or special considerations..."></textarea>
                </div>

                <!-- Media & Files Section -->
                <div class="media-section mb-4">
                    <h6 class="section-title mb-3">
                        <i class="bi bi-file-earmark-media me-2"></i>Media & Attachments
                    </h6>

                    <div class="row">
                        <!-- Video URL -->
                        <div class="col-md-12 mb-3">
                            <label class="form-label">Video URL (YouTube/Vimeo)</label>
                            <input v-model="exerciseForm.video_url" type="url" class="form-control"
                                placeholder="https://www.youtube.com/watch?v=...">
                            <small class="text-muted">Paste a YouTube or Vimeo link for the exercise
                                demonstration</small>
                        </div>
                    </div>

                    <div class="row">
                        <!-- Image Upload -->
                        <div class="col-md-6 mb-3">
                            <label class="form-label">Exercise Image</label>
                            <div class="file-upload-box" @click="triggerFileInput('image')"
                                @drop.prevent="handleFileDrop($event, 'image')" @dragover.prevent @dragenter.prevent>
                                <div v-if="exerciseForm.image_path || imagePreview" class="file-preview">
                                    <img :src="imagePreview || getFilePath(exerciseForm.image_path)"
                                        alt="Exercise image" class="preview-image">
                                    <button type="button" class="btn-remove-file" @click.stop="removeFile('image')">
                                        <i class="bi bi-x-lg"></i>
                                    </button>
                                </div>
                                <div v-else class="upload-placeholder">
                                    <i class="bi bi-image"></i>
                                    <span>Click or drag image here</span>
                                    <small>JPG, PNG, GIF, WebP (max 10MB)</small>
                                </div>
                                <input type="file" ref="imageInput" @change="handleFileSelect($event, 'image')"
                                    accept="image/jpeg,image/png,image/gif,image/webp" class="d-none">
                            </div>
                            <div v-if="uploadingImage" class="upload-progress mt-2">
                                <div class="spinner-border spinner-border-sm me-2"></div>
                                <span>Uploading image...</span>
                            </div>
                        </div>

                        <!-- PDF Upload -->
                        <div class="col-md-6 mb-3">
                            <label class="form-label">PDF Instructions</label>
                            <div class="file-upload-box" @click="triggerFileInput('pdf')"
                                @drop.prevent="handleFileDrop($event, 'pdf')" @dragover.prevent @dragenter.prevent>
                                <div v-if="exerciseForm.pdf_path" class="file-preview file-preview-pdf">
                                    <i class="bi bi-file-earmark-pdf"></i>
                                    <span class="file-name">{{ getFileName(exerciseForm.pdf_path) }}</span>
                                    <button type="button" class="btn-remove-file" @click.stop="removeFile('pdf')">
                                        <i class="bi bi-x-lg"></i>
                                    </button>
                                </div>
                                <div v-else class="upload-placeholder">
                                    <i class="bi bi-file-earmark-pdf"></i>
                                    <span>Click or drag PDF here</span>
                                    <small>PDF files only (max 20MB)</small>
                                </div>
                                <input type="file" ref="pdfInput" @change="handleFileSelect($event, 'pdf')"
                                    accept="application/pdf" class="d-none">
                            </div>
                            <div v-if="uploadingPdf" class="upload-progress mt-2">
                                <div class="spinner-border spinner-border-sm me-2"></div>
                                <span>Uploading PDF...</span>
                            </div>
                        </div>
                    </div>

                    <!-- Additional Attachments -->
                    <div class="mb-3">
                        <label class="form-label">Additional Attachments</label>
                        <div class="attachments-list"
                            v-if="exerciseForm.attachments && exerciseForm.attachments.length > 0">
                            <div v-for="(attachment, idx) in exerciseForm.attachments" :key="idx"
                                class="attachment-item">
                                <i :class="getAttachmentIcon(attachment.type)"></i>
                                <span class="attachment-name">{{ attachment.name }}</span>
                                <span class="attachment-size">{{ formatFileSize(attachment.size) }}</span>
                                <button type="button" class="btn-remove-attachment" @click="removeAttachment(idx)">
                                    <i class="bi bi-x"></i>
                                </button>
                            </div>
                        </div>
                        <div class="file-upload-box file-upload-attachments" @click="triggerFileInput('attachment')"
                            @drop.prevent="handleFileDrop($event, 'attachment')" @dragover.prevent @dragenter.prevent>
                            <div class="upload-placeholder">
                                <i class="bi bi-paperclip"></i>
                                <span>Add attachment</span>
                                <small>Images, PDFs, Documents (max 50MB each)</small>
                            </div>
                            <input type="file" ref="attachmentInput" @change="handleFileSelect($event, 'attachment')"
                                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.mp4,.mov" class="d-none"
                                multiple>
                        </div>
                        <div v-if="uploadingAttachment" class="upload-progress mt-2">
                            <div class="spinner-border spinner-border-sm me-2"></div>
                            <span>Uploading attachment...</span>
                        </div>
                    </div>
                </div>

                <div class="d-flex justify-content-end gap-2 mt-4">
                    <button type="button" class="btn btn-secondary" @click="showExerciseModal = false">
                        Cancel
                    </button>
                    <button type="submit" class="btn btn-primary"
                        :disabled="savingExercise || uploadingImage || uploadingPdf || uploadingAttachment">
                        <span v-if="savingExercise">
                            <span class="spinner-border spinner-border-sm me-1"></span>
                            Saving...
                        </span>
                        <span v-else>
                            <i class="bi bi-check-lg me-1"></i>
                            {{ editingExercise ? 'Update Exercise' : 'Add Exercise' }}
                        </span>
                    </button>
                </div>
            </form>
        </b-modal>

        <!-- Duplicate Exercise Modal -->
        <b-modal v-model="showDuplicateModal" title="Duplicate Exercise" size="md" hide-footer>
            <p>Create a copy of <strong>{{ duplicatingExercise?.exercise_name }}</strong></p>

            <div class="mb-3">
                <label class="form-label">New Exercise Name</label>
                <input v-model="duplicateForm.new_name" type="text" class="form-control">
            </div>

            <div class="row">
                <div class="col-md-6 mb-3">
                    <label class="form-label">Target Phase</label>
                    <select v-model="duplicateForm.phase_number" class="form-select">
                        <option v-for="phase in 5" :key="phase" :value="phase">
                            Phase {{ phase }} - {{ getPhaseInfo(phase).name }}
                        </option>
                    </select>
                </div>
                <div class="col-md-6 mb-3">
                    <label class="form-label">Maturity Category</label>
                    <select v-model="duplicateForm.maturity_category" class="form-select">
                        <option :value="null">All Players</option>
                        <option value="Pre-PHV">Pre-PHV</option>
                        <option value="Circa-PHV">Circa-PHV</option>
                        <option value="Post-PHV">Post-PHV</option>
                    </select>
                </div>
            </div>

            <div class="d-flex justify-content-end gap-2 mt-3">
                <button class="btn btn-secondary" @click="showDuplicateModal = false">Cancel</button>
                <button class="btn btn-primary" :disabled="duplicatingExerciseLoading" @click="duplicateExercise">
                    <span v-if="duplicatingExerciseLoading">
                        <span class="spinner-border spinner-border-sm me-1"></span>
                        Duplicating...
                    </span>
                    <span v-else>
                        <i class="bi bi-copy me-1"></i>
                        Duplicate
                    </span>
                </button>
            </div>
        </b-modal>

        <!-- Delete Confirmation Modal -->
        <b-modal v-model="showDeleteModal" title="Delete Exercise" size="sm" hide-footer>
            <p>Are you sure you want to delete <strong>{{ deletingExercise?.exercise_name }}</strong>?</p>
            <p class="text-muted small">This will deactivate the exercise. It can be reactivated later if needed.</p>

            <div class="d-flex justify-content-end gap-2 mt-3">
                <button class="btn btn-secondary" @click="showDeleteModal = false">Cancel</button>
                <button class="btn btn-danger" :disabled="deletingExerciseLoading" @click="deleteExercise">
                    <span v-if="deletingExerciseLoading">
                        <span class="spinner-border spinner-border-sm me-1"></span>
                        Deleting...
                    </span>
                    <span v-else>
                        <i class="bi bi-trash me-1"></i>
                        Delete
                    </span>
                </button>
            </div>
        </b-modal>

        <!-- Tactical Board Picker Modal -->
        <TacticalBoardPicker
            :show="showTacticalBoardPicker"
            :alreadyLinkedIds="tacticalBoardExerciseId ? (exerciseTacticalBoards[tacticalBoardExerciseId] || []).map(b => b.id) : []"
            defaultCategory="rtp"
            @close="showTacticalBoardPicker = false"
            @boards-selected="onTacticalBoardsSelected"
        />
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ReturnToPlayService, RTP_PHASES } from "@/services/rtp-service.js";
import TacticalBoardPicker from "../TacticalBoard/TacticalBoardPicker.vue";
import TacticalBoardCanvas from "../TacticalBoard/TacticalBoardCanvas.vue";
import { TacticalBoardService } from "@/services/tactical-board-service.js";

export default {
    name: "RTPExerciseLibrary",
    components: {
        TacticalBoardPicker,
        TacticalBoardCanvas,
    },
    props: {
        currentPhase: {
            type: [Number, String],
            default: null,
        },
        maturityCategory: {
            type: String,
            default: null,
        },
        // Assignment mode props - when viewing a player's RTP
        assignmentMode: {
            type: Boolean,
            default: false,
        },
        injuryId: {
            type: [Number, String],
            default: null,
        },
        playerId: {
            type: [Number, String],
            default: null,
        },
        playerName: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            loading: true,
            selectedPhase: null,
            exercises: [],
            expandedCategories: ["aerobic", "strength"],
            expandedExercises: [],
            userData: null,

            // Admin mode
            adminMode: false,

            // Assignment tracking
            assignedExercises: [],
            loadingAssignments: false,
            assigningExercise: null,

            // Exercise form modal
            showExerciseModal: false,
            editingExercise: null,
            savingExercise: false,
            exerciseForm: this.getEmptyExerciseForm(),

            // File upload states
            uploadingImage: false,
            uploadingPdf: false,
            uploadingAttachment: false,
            imagePreview: null,

            // Duplicate modal
            showDuplicateModal: false,
            duplicatingExercise: null,
            duplicatingExerciseLoading: false,
            duplicateForm: {
                new_name: '',
                phase_number: null,
                maturity_category: null
            },

            // Tactical boards for exercises
            exerciseTacticalBoards: {},
            showTacticalBoardPicker: false,
            tacticalBoardExerciseId: null,
            loadingTacticalBoards: false,

            // Delete modal
            showDeleteModal: false,
            deletingExercise: null,
            deletingExerciseLoading: false,

            exerciseCategories: [
                {
                    key: "aerobic",
                    name: "Aerobic / Cardiovascular",
                    icon: "bi bi-heart-pulse",
                },
                {
                    key: "strength",
                    name: "Strength & Conditioning",
                    icon: "bi bi-lightning",
                },
                {
                    key: "mobility",
                    name: "Mobility & Flexibility",
                    icon: "bi bi-arrows-move",
                },
                {
                    key: "sport_specific",
                    name: "Sport-Specific Drills",
                    icon: "bi bi-dribbble",
                },
                {
                    key: "balance",
                    name: "Balance & Proprioception",
                    icon: "bi bi-bullseye",
                },
                { key: "agility", name: "Agility & Speed", icon: "bi bi-wind" },
                {
                    key: "contact",
                    name: "Contact & Match Practice",
                    icon: "bi bi-people",
                },
            ],
        };
    },
    computed: {
        ...mapGetters(["getProfile"]),

        canManageExercises() {
            if (!this.userData) return false;
            const allowedRoles = ['Admin', 'Head Physio', 'Head Sports Science', 'Physio', 'Sports Science'];
            return allowedRoles.includes(this.userData.userType) ||
                allowedRoles.includes(this.userData.position);
        },

        phaseExercises() {
            return this.exercises.filter((e) => e.phase_number == this.selectedPhase);
        },
        // Get IDs of assigned exercises for quick lookup
        assignedExerciseIds() {
            return this.assignedExercises.map(a => parseInt(a.exercise_id));
        },
    },
    watch: {
        currentPhase: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.selectedPhase = parseInt(newVal);
                } else {
                    this.selectedPhase = 1;
                }
            },
        },
        getProfile: {
            immediate: true,
            handler(profile) {
                if (profile) {
                    this.userData = profile;
                }
            },
        },
        injuryId: {
            handler(newVal) {
                if (newVal && this.assignmentMode) {
                    this.loadAssignedExercises();
                }
            },
        },
    },
    async mounted() {
        // Set userData from profile
        if (this.getProfile) {
            this.userData = this.getProfile;
        }
        await this.loadExercises();
        // Load assignments if in assignment mode
        if (this.assignmentMode && this.injuryId) {
            await this.loadAssignedExercises();
        }
    },
    methods: {
        getPhaseInfo(phase) {
            const baseInfo = RTP_PHASES[phase] || {};

            // Extended phase information for exercise library
            const extendedInfo = {
                1: {
                    minDuration: "Minimum 24-48 hours",
                    leadRole: "Physio / Medical",
                    objective: "Pain-free daily activities and early mobilisation",
                    progressionCriteria: [
                        "Pain-free at rest",
                        "Minimal swelling/inflammation",
                        "Full passive range of motion",
                        "Able to walk pain-free",
                    ],
                },
                2: {
                    minDuration: "Minimum 48-72 hours",
                    leadRole: "Physio / S&C",
                    objective: "Light aerobic conditioning without aggravating injury",
                    progressionCriteria: [
                        "Complete Phase 1 activities pain-free",
                        "Tolerate light aerobic activity for 15-20 mins",
                        "No delayed onset muscle soreness",
                        "Full active range of motion",
                    ],
                },
                3: {
                    minDuration: "Minimum 3-5 days",
                    leadRole: "S&C / Coaching",
                    objective: "Introduction of sport-specific movements and ball work",
                    progressionCriteria: [
                        "Complete Phase 2 activities pain-free",
                        "Sport-specific drills without discomfort",
                        "Confident in cutting/turning movements",
                        "No compensatory movement patterns",
                    ],
                },
                4: {
                    minDuration: "Minimum 5-7 days",
                    leadRole: "S&C / Head Coach",
                    objective: "Full training participation without contact",
                    progressionCriteria: [
                        "Complete Phase 3 activities at full intensity",
                        "Psychological readiness for contact",
                        "Pass all functional movement tests",
                        "Coach approval for contact progression",
                    ],
                },
                5: {
                    minDuration: "Final clearance required",
                    leadRole: "Head Sports Science / Medical",
                    objective: "Full contact training and match readiness",
                    progressionCriteria: [
                        "Complete full contact training sessions",
                        "No adverse reactions over 48-72 hours",
                        "Match fitness achieved",
                        "Final medical clearance obtained",
                    ],
                },
            };

            return { ...baseInfo, ...extendedInfo[phase] };
        },

        async loadExercises() {
            this.loading = true;
            try {
                const params = {};
                if (this.userData?.clubId) {
                    params.clubId = this.userData.clubId;
                }
                if (this.adminMode) {
                    params.include_inactive = true;
                }
                const response = await ReturnToPlayService.getExerciseLibrary(params);
                if (response.data.success) {
                    this.exercises = response.data.exercises || [];
                } else {
                    // Use default exercises if API fails
                    this.exercises = this.getDefaultExercises();
                }
            } catch (error) {
                console.error("Error loading exercises:", error);
                this.exercises = this.getDefaultExercises();
            } finally {
                this.loading = false;
            }
        },

        getDefaultExercises() {
            // Fallback exercise library
            return [
                // Phase 1 - Acute/Rest
                {
                    id: 1,
                    phase: 1,
                    category: "mobility",
                    exercise_name: "Passive ROM exercises",
                    progression_level: "Easy",
                    description:
                        "Gentle passive range of motion exercises within pain-free limits",
                    sets_reps: "2 x 10-15 reps",
                    duration: "10-15 mins",
                    intensity: "Low (pain-free)",
                },
                {
                    id: 2,
                    phase: 1,
                    category: "aerobic",
                    exercise_name: "Pool walking",
                    progression_level: "Easy",
                    description:
                        "Gentle walking in shallow water to maintain cardiovascular fitness without load",
                    duration: "10-15 mins",
                    intensity: "Very Low",
                },
                {
                    id: 3,
                    phase: 1,
                    category: "strength",
                    exercise_name: "Isometric holds (pain-free)",
                    progression_level: "Easy",
                    description: "Static muscle contractions without joint movement",
                    sets_reps: "3-5 x 10-15 sec holds",
                    intensity: "Low (50% max)",
                },

                // Phase 2 - Light Aerobic
                {
                    id: 4,
                    phase: 2,
                    category: "aerobic",
                    exercise_name: "Stationary cycling",
                    progression_level: "Easy",
                    description:
                        "Low resistance cycling to improve cardiovascular fitness",
                    duration: "15-20 mins",
                    intensity: "50-60% HR max",
                },
                {
                    id: 5,
                    phase: 2,
                    category: "aerobic",
                    exercise_name: "Pool swimming",
                    progression_level: "Intermediate",
                    description: "Front crawl or breaststroke as tolerated",
                    duration: "15-20 mins",
                    intensity: "55-65% HR max",
                },
                {
                    id: 6,
                    phase: 2,
                    category: "strength",
                    exercise_name: "Bodyweight squats",
                    progression_level: "Easy",
                    description: "Controlled bodyweight squats focusing on form",
                    sets_reps: "3 x 10-15",
                    intensity: "Bodyweight only",
                },
                {
                    id: 7,
                    phase: 2,
                    category: "mobility",
                    exercise_name: "Dynamic stretching routine",
                    progression_level: "Easy",
                    description: "Full body dynamic stretches",
                    duration: "10 mins",
                    intensity: "Light",
                },
                {
                    id: 8,
                    phase: 2,
                    category: "balance",
                    exercise_name: "Single leg balance",
                    progression_level: "Easy",
                    description: "Single leg standing, progress to unstable surface",
                    duration: "30-60 sec each leg",
                    sets_reps: "3 x each leg",
                },

                // Phase 3 - Sport-Specific
                {
                    id: 9,
                    phase: 3,
                    category: "aerobic",
                    exercise_name: "Jogging intervals",
                    progression_level: "Easy",
                    description: "Alternating jog/walk intervals on flat surface",
                    duration: "20-25 mins",
                    intensity: "60-70% HR max",
                },
                {
                    id: 10,
                    phase: 3,
                    category: "sport_specific",
                    exercise_name: "Ball work - stationary",
                    progression_level: "Easy",
                    description: "Passing and receiving drills without movement",
                    duration: "15-20 mins",
                    intensity: "Technical focus",
                },
                {
                    id: 11,
                    phase: 3,
                    category: "sport_specific",
                    exercise_name: "Ball work - with movement",
                    progression_level: "Intermediate",
                    description: "Dribbling and passing while moving at controlled pace",
                    duration: "15-20 mins",
                    intensity: "Moderate",
                },
                {
                    id: 12,
                    phase: 3,
                    category: "agility",
                    exercise_name: "Linear running progressions",
                    progression_level: "Easy",
                    description: "Straight line running at increasing speeds",
                    duration: "10-15 mins",
                    intensity: "60-80% max speed",
                },
                {
                    id: 13,
                    phase: 3,
                    category: "agility",
                    exercise_name: "Controlled cutting drills",
                    progression_level: "Intermediate",
                    description: "45-degree cutting movements at controlled pace",
                    sets_reps: "3 x 6 reps each direction",
                    intensity: "60-70% max speed",
                },
                {
                    id: 14,
                    phase: 3,
                    category: "strength",
                    exercise_name: "Resistance band exercises",
                    progression_level: "Intermediate",
                    description: "Sport-specific resistance exercises",
                    sets_reps: "3 x 12-15",
                    intensity: "Medium resistance",
                },

                // Phase 4 - Non-Contact Training
                {
                    id: 15,
                    phase: 4,
                    category: "aerobic",
                    exercise_name: "High-intensity running",
                    progression_level: "Intermediate",
                    description: "Interval training at match-like intensities",
                    duration: "25-30 mins",
                    intensity: "75-85% HR max",
                },
                {
                    id: 16,
                    phase: 4,
                    category: "agility",
                    exercise_name: "Sharp direction changes",
                    progression_level: "Intermediate",
                    description: "Quick 90-degree cuts and changes of direction",
                    sets_reps: "4 x 8 reps",
                    intensity: "80-90% max speed",
                },
                {
                    id: 17,
                    phase: 4,
                    category: "agility",
                    exercise_name: "Deceleration training",
                    progression_level: "Advanced",
                    description: "Sprint and rapid deceleration drills",
                    sets_reps: "3 x 6 reps",
                    intensity: "Near maximal",
                },
                {
                    id: 18,
                    phase: 4,
                    category: "sport_specific",
                    exercise_name: "Full training drills (no contact)",
                    progression_level: "Intermediate",
                    description: "Participation in team drills avoiding physical contact",
                    duration: "45-60 mins",
                    intensity: "Match-like",
                },
                {
                    id: 19,
                    phase: 4,
                    category: "strength",
                    exercise_name: "Power exercises",
                    progression_level: "Advanced",
                    description: "Plyometrics, jumping, bounding",
                    sets_reps: "3-4 x 6-8",
                    intensity: "High",
                },
                {
                    id: 20,
                    phase: 4,
                    category: "sport_specific",
                    exercise_name: "Position-specific drills",
                    progression_level: "Advanced",
                    description: "Role-specific training activities",
                    duration: "20-30 mins",
                    intensity: "High",
                },

                // Phase 5 - Full Contact/Clearance
                {
                    id: 21,
                    phase: 5,
                    category: "contact",
                    exercise_name: "Light contact drills",
                    progression_level: "Intermediate",
                    description: "Gradually introduced physical challenges",
                    duration: "15-20 mins",
                    intensity: "Progressive",
                    precautions: "Monitor for pain/anxiety response",
                },
                {
                    id: 22,
                    phase: 5,
                    category: "contact",
                    exercise_name: "Full contact training",
                    progression_level: "Advanced",
                    description: "Complete participation in all contact drills",
                    duration: "Full session",
                    intensity: "Match-like",
                },
                {
                    id: 23,
                    phase: 5,
                    category: "sport_specific",
                    exercise_name: "Match simulation",
                    progression_level: "Advanced",
                    description: "In-house match or high-intensity scrimmage",
                    duration: "60-90 mins",
                    intensity: "Match",
                },
                {
                    id: 24,
                    phase: 5,
                    category: "aerobic",
                    exercise_name: "Match fitness testing",
                    progression_level: "Advanced",
                    description: "Yo-Yo test or similar match fitness assessment",
                    duration: "20-30 mins",
                    intensity: "Maximal",
                },
            ];
        },

        toggleCategory(categoryKey) {
            const idx = this.expandedCategories.indexOf(categoryKey);
            if (idx > -1) {
                this.expandedCategories.splice(idx, 1);
            } else {
                this.expandedCategories.push(categoryKey);
            }
        },

        toggleExercise(exerciseId) {
            const idx = this.expandedExercises.indexOf(exerciseId);
            if (idx > -1) {
                this.expandedExercises.splice(idx, 1);
            } else {
                this.expandedExercises.push(exerciseId);
                // Load tactical boards when exercise is expanded
                if (!this.exerciseTacticalBoards[exerciseId]) {
                    this.loadTacticalBoardsForExercise(exerciseId);
                }
            }
        },

        getCategoryExercises(categoryKey) {
            let exercises = this.phaseExercises.filter((e) => e.category === categoryKey);
            // In non-admin mode, only show active exercises
            if (!this.adminMode) {
                exercises = exercises.filter((e) => e.is_active !== false && e.is_active !== 0);
            }
            return exercises;
        },

        getExerciseCount(categoryKey) {
            return this.getCategoryExercises(categoryKey).length;
        },

        // ========================================================================
        // EXERCISE ASSIGNMENT METHODS
        // ========================================================================

        async loadAssignedExercises() {
            if (!this.injuryId || !this.playerId) return;

            this.loadingAssignments = true;
            try {
                const response = await ReturnToPlayService.getPlayerAssignedExercises({
                    injury_id: this.injuryId,
                    player_id: this.playerId
                });
                if (response.data.success) {
                    this.assignedExercises = response.data.exercises || [];
                }
            } catch (error) {
                console.error('Error loading assigned exercises:', error);
            } finally {
                this.loadingAssignments = false;
            }
        },

        isExerciseAssigned(exerciseId) {
            return this.assignedExerciseIds.includes(parseInt(exerciseId));
        },

        getAssignmentForExercise(exerciseId) {
            return this.assignedExercises.find(a => parseInt(a.exercise_id) === parseInt(exerciseId));
        },

        async assignExercise(exercise) {
            if (this.assigningExercise) return;

            this.assigningExercise = exercise.id;
            try {
                const response = await ReturnToPlayService.assignExerciseToPlayer({
                    injury_id: this.injuryId,
                    player_id: this.playerId,
                    exercise_id: exercise.id,
                    phase_number: this.selectedPhase,
                    assigned_by: this.userData?.id
                });

                if (response.data.success) {
                    this.$toast?.success(`${exercise.exercise_name} assigned to ${this.playerName}`);
                    await this.loadAssignedExercises();
                } else {
                    this.$toast?.error(response.data.message || 'Failed to assign exercise');
                }
            } catch (error) {
                console.error('Error assigning exercise:', error);
                this.$toast?.error('Error assigning exercise');
            } finally {
                this.assigningExercise = null;
            }
        },

        async unassignExercise(exercise) {
            const assignment = this.getAssignmentForExercise(exercise.id);
            if (!assignment) return;

            this.assigningExercise = exercise.id;
            try {
                const response = await ReturnToPlayService.removeExerciseAssignment(
                    assignment.id,
                    this.userData?.id
                );

                if (response.data.success) {
                    this.$toast?.success(`${exercise.exercise_name} removed from ${this.playerName}'s exercises`);
                    await this.loadAssignedExercises();
                } else {
                    this.$toast?.error(response.data.message || 'Failed to remove exercise');
                }
            } catch (error) {
                console.error('Error removing exercise:', error);
                this.$toast?.error('Error removing exercise');
            } finally {
                this.assigningExercise = null;
            }
        },

        toggleExerciseAssignment(exercise) {
            if (this.isExerciseAssigned(exercise.id)) {
                this.unassignExercise(exercise);
            } else {
                this.assignExercise(exercise);
            }
        },

        // Admin methods
        getEmptyExerciseForm() {
            return {
                exercise_name: '',
                phase_number: this.selectedPhase || 1,
                category: 'strength',
                description: '',
                sets_reps: '',
                duration: '',
                intensity: '',
                difficulty: 'Intermediate',
                precautions: '',
                video_url: '',
                image_path: '',
                pdf_path: '',
                attachments: [],
                maturity_category: null,
                sort_order: 0
            };
        },

        toggleAdminMode() {
            this.adminMode = !this.adminMode;
            // Reload exercises to include/exclude inactive ones
            this.loadExercises();
        },

        openAddExerciseModal() {
            this.editingExercise = null;
            this.exerciseForm = this.getEmptyExerciseForm();
            this.showExerciseModal = true;
        },

        openEditExerciseModal(exercise) {
            this.editingExercise = exercise;
            this.imagePreview = null;
            this.exerciseForm = {
                exercise_name: exercise.exercise_name || '',
                phase_number: exercise.phase_number || 1,
                category: exercise.category || 'strength',
                description: exercise.description || '',
                sets_reps: exercise.sets_reps || '',
                duration: exercise.duration || '',
                intensity: exercise.intensity || '',
                difficulty: exercise.difficulty || 'Intermediate',
                precautions: exercise.precautions || '',
                video_url: exercise.video_url || '',
                image_path: exercise.image_path || '',
                pdf_path: exercise.pdf_path || '',
                attachments: exercise.attachments ? (typeof exercise.attachments === 'string' ? JSON.parse(exercise.attachments) : exercise.attachments) : [],
                maturity_category: exercise.maturity_category || null,
                sort_order: exercise.sort_order || 0
            };
            this.showExerciseModal = true;
        },

        async saveExercise() {
            if (!this.exerciseForm.exercise_name) {
                this.$toast?.error('Exercise name is required');
                return;
            }

            this.savingExercise = true;
            try {
                const data = {
                    ...this.exerciseForm,
                    clubId: this.userData?.clubId || null
                };

                let response;
                if (this.editingExercise) {
                    // Update existing
                    data.id = this.editingExercise.id;
                    response = await ReturnToPlayService.updateExercise(data);
                } else {
                    // Add new
                    response = await ReturnToPlayService.addExercise(data);
                }

                if (response.data.success) {
                    this.$toast?.success(this.editingExercise ? 'Exercise updated successfully' : 'Exercise added successfully');
                    this.showExerciseModal = false;
                    await this.loadExercises();
                } else {
                    this.$toast?.error(response.data.error || 'Failed to save exercise');
                }
            } catch (error) {
                console.error('Error saving exercise:', error);
                this.$toast?.error('Error saving exercise');
            } finally {
                this.savingExercise = false;
            }
        },

        openDuplicateModal(exercise) {
            this.duplicatingExercise = exercise;
            this.duplicateForm = {
                new_name: `${exercise.exercise_name} (Copy)`,
                phase_number: exercise.phase_number,
                maturity_category: null
            };
            this.showDuplicateModal = true;
        },

        async duplicateExercise() {
            if (!this.duplicateForm.new_name) {
                this.$toast?.error('Please enter a name for the duplicate');
                return;
            }

            this.duplicatingExerciseLoading = true;
            try {
                const response = await ReturnToPlayService.duplicateExercise(
                    this.duplicatingExercise.id,
                    this.userData?.clubId || null,
                    {
                        new_name: this.duplicateForm.new_name,
                        phase_number: this.duplicateForm.phase_number,
                        maturity_category: this.duplicateForm.maturity_category
                    }
                );

                if (response.data.success) {
                    this.$toast?.success('Exercise duplicated successfully');
                    this.showDuplicateModal = false;
                    await this.loadExercises();
                } else {
                    this.$toast?.error(response.data.error || 'Failed to duplicate exercise');
                }
            } catch (error) {
                console.error('Error duplicating exercise:', error);
                this.$toast?.error('Error duplicating exercise');
            } finally {
                this.duplicatingExerciseLoading = false;
            }
        },

        confirmDeleteExercise(exercise) {
            this.deletingExercise = exercise;
            this.showDeleteModal = true;
        },

        async deleteExercise() {
            this.deletingExerciseLoading = true;
            try {
                const response = await ReturnToPlayService.deleteExercise(
                    this.deletingExercise.id,
                    this.userData?.clubId || null
                );

                if (response.data.success) {
                    this.$toast?.success('Exercise deleted successfully');
                    this.showDeleteModal = false;
                    await this.loadExercises();
                } else {
                    this.$toast?.error(response.data.error || 'Failed to delete exercise');
                }
            } catch (error) {
                console.error('Error deleting exercise:', error);
                this.$toast?.error('Error deleting exercise');
            } finally {
                this.deletingExerciseLoading = false;
            }
        },

        async toggleExerciseActive(exercise) {
            try {
                const response = await ReturnToPlayService.toggleExerciseActive(
                    exercise.id,
                    this.userData?.clubId || null
                );

                if (response.data.success) {
                    const newStatus = response.data.is_active ? 'activated' : 'deactivated';
                    this.$toast?.success(`Exercise ${newStatus} successfully`);
                    await this.loadExercises();
                } else {
                    this.$toast?.error(response.data.error || 'Failed to toggle exercise status');
                }
            } catch (error) {
                console.error('Error toggling exercise status:', error);
                this.$toast?.error('Error toggling exercise status');
            }
        },

        // ========================================================================
        // FILE UPLOAD HANDLERS
        // ========================================================================

        /**
         * Trigger the hidden file input for a specific type
         * @param {string} type - 'image', 'pdf', or 'attachment'
         */
        triggerFileInput(type) {
            const refMap = {
                image: 'imageInput',
                pdf: 'pdfInput',
                attachment: 'attachmentInput'
            };
            const inputRef = this.$refs[refMap[type]];
            if (inputRef) {
                inputRef.click();
            }
        },

        /**
         * Handle file selection from input
         * @param {Event} event - The input change event
         * @param {string} type - 'image', 'pdf', or 'attachment'
         */
        async handleFileSelect(event, type) {
            const files = event.target.files;
            if (!files || files.length === 0) return;

            if (type === 'attachment') {
                // Handle multiple attachments
                for (const file of files) {
                    await this.uploadFile(file, type);
                }
            } else {
                // Handle single file (image or pdf)
                await this.uploadFile(files[0], type);
            }

            // Reset the input so the same file can be selected again
            event.target.value = '';
        },

        /**
         * Handle file drop from drag and drop
         * @param {DragEvent} event - The drop event
         * @param {string} type - 'image', 'pdf', or 'attachment'
         */
        async handleFileDrop(event, type) {
            const files = event.dataTransfer.files;
            if (!files || files.length === 0) return;

            if (type === 'attachment') {
                // Handle multiple attachments
                for (const file of files) {
                    await this.uploadFile(file, type);
                }
            } else {
                // Handle single file (image or pdf)
                await this.uploadFile(files[0], type);
            }
        },

        /**
         * Upload a file to the server
         * @param {File} file - The file to upload
         * @param {string} type - 'image', 'pdf', or 'attachment'
         */
        async uploadFile(file, type) {
            // Validate file type
            const allowedTypes = {
                image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
                pdf: ['application/pdf'],
                attachment: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf',
                    'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                    'video/mp4', 'video/quicktime']
            };

            const maxSizes = {
                image: 10 * 1024 * 1024,      // 10MB
                pdf: 20 * 1024 * 1024,        // 20MB
                attachment: 50 * 1024 * 1024  // 50MB
            };

            if (!allowedTypes[type].includes(file.type)) {
                this.$toast?.error(`Invalid file type for ${type}. Please select a valid file.`);
                return;
            }

            if (file.size > maxSizes[type]) {
                const maxMB = maxSizes[type] / (1024 * 1024);
                this.$toast?.error(`File too large. Maximum size is ${maxMB}MB`);
                return;
            }

            // Set uploading state
            if (type === 'image') this.uploadingImage = true;
            else if (type === 'pdf') this.uploadingPdf = true;
            else this.uploadingAttachment = true;

            try {
                // Convert file to base64
                const base64Data = await this.fileToBase64(file);

                // Upload to server
                const response = await ReturnToPlayService.uploadExerciseFile(
                    this.userData?.clubId || null,
                    file.name,
                    base64Data,
                    type === 'attachment' ? 'attachment' : type
                );

                if (response.data.success) {
                    const filePath = response.data.file_path;

                    if (type === 'image') {
                        this.exerciseForm.image_path = filePath;
                        this.imagePreview = URL.createObjectURL(file);
                    } else if (type === 'pdf') {
                        this.exerciseForm.pdf_path = filePath;
                    } else {
                        // Add to attachments array
                        if (!this.exerciseForm.attachments) {
                            this.exerciseForm.attachments = [];
                        }
                        this.exerciseForm.attachments.push({
                            name: response.data.original_name || file.name,
                            path: filePath,
                            type: file.type,
                            size: file.size
                        });
                    }

                    this.$toast?.success(`${type.charAt(0).toUpperCase() + type.slice(1)} uploaded successfully`);
                } else {
                    this.$toast?.error(response.data.message || `Failed to upload ${type}`);
                }
            } catch (error) {
                console.error(`Error uploading ${type}:`, error);
                this.$toast?.error(`Error uploading ${type}`);
            } finally {
                // Reset uploading state
                if (type === 'image') this.uploadingImage = false;
                else if (type === 'pdf') this.uploadingPdf = false;
                else this.uploadingAttachment = false;
            }
        },

        /**
         * Convert a file to base64 string
         * @param {File} file - The file to convert
         * @returns {Promise<string>} - Base64 encoded string (without data URI prefix)
         */
        fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    // Remove the data URI prefix (e.g., "data:image/png;base64,")
                    const base64 = reader.result.split(',')[1];
                    resolve(base64);
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        },

        /**
         * Remove a file from the form
         * @param {string} type - 'image' or 'pdf'
         */
        removeFile(type) {
            if (type === 'image') {
                this.exerciseForm.image_path = '';
                this.imagePreview = null;
            } else if (type === 'pdf') {
                this.exerciseForm.pdf_path = '';
            }
        },

        /**
         * Remove an attachment from the attachments array
         * @param {number} idx - Index of the attachment to remove
         */
        removeAttachment(idx) {
            if (this.exerciseForm.attachments && this.exerciseForm.attachments.length > idx) {
                this.exerciseForm.attachments.splice(idx, 1);
            }
        },

        // ========================================================================
        // FILE UTILITY METHODS
        // ========================================================================

        /**
         * Get full URL for a file path
         * @param {string} path - Relative file path
         * @returns {string} - Full URL
         */
        getFilePath(path) {
            if (!path) return '';
            // If already a full URL, return as-is
            if (path.startsWith('http://') || path.startsWith('https://')) {
                return path;
            }
            // Build full URL based on environment
            const baseUrl = window.location.href.indexOf('8080') > -1
                ? 'http://localhost:8888/'
                : '/';
            return baseUrl + path;
        },

        /**
         * Extract filename from a path
         * @param {string} path - File path
         * @returns {string} - Filename
         */
        getFileName(path) {
            if (!path) return '';
            const parts = path.split('/');
            const filename = parts[parts.length - 1];
            // Remove the unique ID prefix if present (e.g., "abc123_filename.pdf" -> "filename.pdf")
            const underscoreIndex = filename.indexOf('_');
            if (underscoreIndex > 0 && underscoreIndex < 20) {
                return filename.substring(underscoreIndex + 1);
            }
            return filename;
        },

        /**
         * Format file size in human-readable format
         * @param {number} bytes - File size in bytes
         * @returns {string} - Formatted size (e.g., "1.5 MB")
         */
        formatFileSize(bytes) {
            if (!bytes || bytes === 0) return '0 B';
            const units = ['B', 'KB', 'MB', 'GB'];
            let unitIndex = 0;
            let size = bytes;
            while (size >= 1024 && unitIndex < units.length - 1) {
                size /= 1024;
                unitIndex++;
            }
            return `${size.toFixed(1)} ${units[unitIndex]}`;
        },

        /**
         * Get Bootstrap icon class for attachment type
         * @param {string} mimeType - MIME type of the file
         * @returns {string} - Bootstrap icon class
         */
        getAttachmentIcon(mimeType) {
            if (!mimeType) return 'bi bi-file-earmark';

            if (mimeType.startsWith('image/')) {
                return 'bi bi-file-earmark-image';
            } else if (mimeType === 'application/pdf') {
                return 'bi bi-file-earmark-pdf';
            } else if (mimeType.includes('word') || mimeType.includes('document')) {
                return 'bi bi-file-earmark-word';
            } else if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) {
                return 'bi bi-file-earmark-excel';
            } else if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) {
                return 'bi bi-file-earmark-ppt';
            } else if (mimeType.startsWith('video/')) {
                return 'bi bi-file-earmark-play';
            }
            return 'bi bi-file-earmark';
        },

        // ---- Tactical Board Methods ----
        getExerciseBoards(exerciseId) {
            return this.exerciseTacticalBoards[exerciseId] || [];
        },

        async loadTacticalBoardsForExercise(exerciseId) {
            try {
                const res = await TacticalBoardService.getBoardsForExercise(exerciseId);
                if (res.data.success) {
                    this.$set(this.exerciseTacticalBoards, exerciseId, res.data.data || []);
                }
            } catch (err) {
                console.error('Error loading tactical boards for exercise:', err);
            }
        },

        openTacticalBoardPicker(exerciseId) {
            this.tacticalBoardExerciseId = exerciseId;
            this.showTacticalBoardPicker = true;
        },

        async onTacticalBoardsSelected(boards) {
            const exerciseId = this.tacticalBoardExerciseId;
            if (!exerciseId) return;

            for (const board of boards) {
                try {
                    await TacticalBoardService.linkBoardToExercise({
                        board_id: board.id,
                        exercise_id: exerciseId,
                        sort_order: this.getExerciseBoards(exerciseId).length,
                    });
                } catch (err) {
                    console.error('Error linking board to exercise:', err);
                }
            }
            this.loadTacticalBoardsForExercise(exerciseId);
        },

        async unlinkTacticalBoardFromExercise(exerciseId, boardId, idx) {
            if (!confirm('Remove this tactical diagram?')) return;
            try {
                await TacticalBoardService.unlinkBoardFromExercise(boardId, exerciseId);
                const boards = this.exerciseTacticalBoards[exerciseId] || [];
                boards.splice(idx, 1);
                this.$set(this.exerciseTacticalBoards, exerciseId, [...boards]);
            } catch (err) {
                console.error('Error unlinking board:', err);
            }
        },
    },
};
</script>


<style scoped lang="scss">
.rtp-exercise-library {
    padding: 0;
}

.library-header {
    margin-bottom: 20px;

    h4 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0 0 4px 0;
        font-size: 18px;

        i {
            color: #007bff;
        }
    }

    p {
        margin: 0;
        color: #6c757d;
        font-size: 14px;
    }
}

.header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
}

.admin-mode-notice {
    color: #856404;
    background: #fff3cd;
    padding: 8px 12px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
}

.admin-controls {
    display: flex;
    gap: 12px;
    align-items: center;
}

/* Assignment Mode Banner */
.assignment-banner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
    border-radius: 12px;
    margin-bottom: 20px;
    border: 1px solid #a5d6a7;
    flex-wrap: wrap;
    gap: 12px;

    .banner-content {
        display: flex;
        align-items: center;
        gap: 14px;
    }

    .banner-icon {
        width: 44px;
        height: 44px;
        border-radius: 12px;
        background: #28a745;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
    }

    .banner-text {
        strong {
            display: block;
            font-size: 15px;
            color: #1b5e20;
            margin-bottom: 2px;
        }

        p {
            margin: 0;
            font-size: 13px;
            color: #388e3c;

            i {
                font-size: 12px;
            }
        }
    }

    .assignment-stats {
        display: flex;
        gap: 10px;
    }

    .stat-badge {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 14px;
        background: white;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 600;
        color: #28a745;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

        i {
            font-size: 16px;
        }
    }
}

/* Assign Button */
.btn-assign {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    background: #e3f2fd;
    color: #1976d2;
    font-size: 18px;
    margin-right: 8px;

    &:hover:not(:disabled) {
        background: #1976d2;
        color: white;
        transform: scale(1.1);
    }

    &.assigned {
        background: #ffebee;
        color: #d32f2f;

        &:hover:not(:disabled) {
            background: #d32f2f;
            color: white;
        }
    }

    &.loading {
        background: #e0e0e0;
        cursor: not-allowed;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.7;
    }
}

.maturity-alert {
    display: flex;
    gap: 12px;
    padding: 14px 16px;
    background: #fff3e0;
    border-radius: 10px;
    border-left: 4px solid #ff9800;
    margin-bottom: 20px;

    > i {
        font-size: 20px;
        color: #f57c00;
        flex-shrink: 0;
        margin-top: 2px;
    }

    strong {
        display: block;
        margin-bottom: 4px;
        color: #e65100;
    }

    p {
        margin: 0;
        font-size: 13px;
        color: #5d4037;
    }
}

.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 40px;
    color: #6c757d;
}

.phase-nav {
    display: flex;
    gap: 8px;
    margin-bottom: 20px;
    flex-wrap: wrap;
}

.phase-btn {
    padding: 8px 16px;
    border: 2px solid #e0e0e0;
    border-radius: 20px;
    background: white;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        border-color: #007bff;
    }

    &.active {
        color: white;
        border-color: transparent;
    }
}

.exercise-categories {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.category-section {
    background: #f8f9fa;
    border-radius: 10px;
    overflow: hidden;
}

.category-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
        background: #e9ecef;
    }
}

.category-title {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;

    i {
        font-size: 18px;
        color: #007bff;
        width: 24px;
        text-align: center;
    }

    h5 {
        margin: 0;
        font-size: 15px;
        font-weight: 600;
    }
}

.exercise-count {
    font-size: 12px;
    color: #6c757d;
    padding: 2px 8px;
    background: white;
    border-radius: 10px;
}

.category-content {
    padding: 0 16px 16px;
}

.no-exercises {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px;
    color: #9e9e9e;
    font-size: 13px;
    font-style: italic;
}

.exercise-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.exercise-card {
    background: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    transition: all 0.2s ease;

    &.inactive {
        opacity: 0.6;
        background: #f8f9fa;

        .exercise-name {
            text-decoration: line-through;
            color: #6c757d;
        }
    }

    &.assigned {
        border: 2px solid #28a745;
        background: #f0fff4;

        .exercise-name {
            color: #155724;
        }
    }
}

.exercise-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
}

.exercise-info {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    flex-wrap: wrap;
}

.exercise-name {
    font-weight: 500;
    font-size: 14px;
}

.progression-badge {
    font-size: 10px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
    text-transform: uppercase;

    &.level-easy {
        background: #e8f5e9;
        color: #2e7d32;
    }

    &.level-intermediate {
        background: #fff3e0;
        color: #ef6c00;
    }

    &.level-advanced {
        background: #ffebee;
        color: #c62828;
    }
}

.maturity-badge {
    font-size: 10px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
    background: #e3f2fd;
    color: #1565c0;
    text-transform: uppercase;
}

.inactive-badge {
    font-size: 10px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
    background: #f8d7da;
    color: #721c24;
    text-transform: uppercase;
}

.exercise-actions {
    display: flex;
    align-items: center;
    gap: 8px;

    .btn-expand {

        background: #cbdce9;
        color: #5f6970;
    }
}

.admin-actions {
    display: flex;
    gap: 4px;
    margin-right: 8px;

    button {
        color: #403436;
    }
}

.btn-action {
    width: 28px;
    height: 28px;
    border: 1px solid #dee2e6;
    background: white;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    padding: 0;

    &:hover {
        border-color: #007bff;
        color: #007bff;
    }

    &.btn-edit:hover {
        border-color: #007bff;
        color: #007bff;
        background: #e7f1ff;
    }

    &.btn-duplicate:hover {
        border-color: #17a2b8;
        color: #17a2b8;
        background: #d1ecf1;
    }

    &.btn-activate:hover {
        border-color: #28a745;
        color: #28a745;
        background: #d4edda;
    }

    &.btn-deactivate:hover {
        border-color: #ffc107;
        color: #856404;
        background: #fff3cd;
    }

    &.btn-delete:hover {
        border-color: #dc3545;
        color: #dc3545;
        background: #f8d7da;
    }

    i {
        font-size: 13px;
    }
}

.btn-expand {
    width: 28px;
    height: 28px;
    border: none;
    background: #f5f5f5;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
        background: #e0e0e0;
    }

    i {
        font-size: 14px;
    }
}

.exercise-details {
    padding: 0 14px 14px;
    border-top: 1px solid #f0f0f0;
}

.detail-section {
    margin-bottom: 14px;

    strong {
        display: block;
        font-size: 12px;
        color: #6c757d;
        margin-bottom: 4px;
    }

    p {
        margin: 0;
        font-size: 13px;
    }
}

.detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 12px;
    margin-bottom: 14px;
}

.detail-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    background: #f8f9fa;
    border-radius: 8px;

    > i {
        font-size: 16px;
        color: #007bff;
        margin-top: 2px;
    }
}

.detail-label {
    display: block;
    font-size: 10px;
    color: #9e9e9e;
    text-transform: uppercase;
}

.detail-value {
    display: block;
    font-size: 13px;
    font-weight: 500;
}

.precautions-section {
    padding: 12px;
    background: #fff8e1;
    border-radius: 8px;
    margin-bottom: 14px;

    strong {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        color: #f57c00;
        margin-bottom: 6px;

        i {
            font-size: 14px;
        }
    }

    p {
        margin: 0 0 6px 0;
        font-size: 13px;
        color: #5d4037;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .circa-precaution {
        padding-top: 8px;
        border-top: 1px dashed #ffcc80;
    }
}

.video-link {
    .btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }
}

.phase-summary {
    margin-top: 24px;
    padding: 16px;
    background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
    border-radius: 12px;

    h5 {
        margin: 0 0 14px 0;
        font-size: 16px;
    }
}

.summary-content {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 16px;
}

.summary-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;

    i {
        font-size: 16px;
        color: #007bff;
    }
}

.criteria-section {
    strong {
        display: block;
        font-size: 13px;
        margin-bottom: 8px;
    }

    ul {
        margin: 0;
        padding-left: 20px;

        li {
            font-size: 13px;
            margin-bottom: 4px;
            color: #495057;

            &:last-child {
                margin-bottom: 0;
            }
        }
    }
}

/* Transitions */
.slide-enter-active,
.slide-leave-active {
    transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    opacity: 0;
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* File Upload Styles */
.media-section {
    background: #f8f9fa;
    border-radius: 10px;
    padding: 16px;
    border: 1px solid #e9ecef;
}

.section-title {
    font-size: 14px;
    font-weight: 600;
    color: #495057;
    display: flex;
    align-items: center;
}

.file-upload-box {
    border: 2px dashed #dee2e6;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    background: white;
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        border-color: #007bff;
        background: #f8f9ff;
    }

    &.file-upload-attachments {
        min-height: 80px;
        padding: 16px;
    }
}

.upload-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: #6c757d;

    i {
        font-size: 32px;
        color: #adb5bd;
    }

    span {
        font-size: 14px;
        font-weight: 500;
    }

    small {
        font-size: 11px;
        color: #adb5bd;
    }
}

.file-preview {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    &.file-preview-pdf {
        flex-direction: column;
        gap: 8px;

        i {
            font-size: 40px;
            color: #dc3545;
        }

        .file-name {
            font-size: 12px;
            color: #495057;
            max-width: 150px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
}

.preview-image {
    max-width: 100%;
    max-height: 150px;
    border-radius: 6px;
    object-fit: contain;
}

.btn-remove-file {
    position: absolute;
    top: -8px;
    right: -8px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #dc3545;
    color: white;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s;

    &:hover {
        background: #c82333;
        transform: scale(1.1);
    }
}

.upload-progress {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: #007bff;
}

/* Attachments List */
.attachments-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
}

.attachment-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    font-size: 13px;

    > i {
        font-size: 18px;
        color: #6c757d;
        flex-shrink: 0;
    }
}

.attachment-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
}

.attachment-size {
    font-size: 11px;
    color: #adb5bd;
    flex-shrink: 0;
}

.btn-remove-attachment {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #f8d7da;
    color: #dc3545;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
    flex-shrink: 0;

    &:hover {
        background: #dc3545;
        color: white;
    }
}

/* Responsive */
@media (max-width: 576px) {
    .phase-nav {
        justify-content: center;
    }

    .category-header {
        flex-wrap: wrap;
    }

    .exercise-count {
        order: 3;
        width: 100%;
        margin-top: 4px;
        text-align: center;
    }

    .detail-grid {
        grid-template-columns: 1fr;
    }

    .summary-content {
        flex-direction: column;
    }

    .file-upload-box {
        min-height: 100px;
        padding: 14px;
    }

    .upload-placeholder i {
        font-size: 24px;
    }
}

/* Tactical Diagrams Section */
.tactical-diagrams-section {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e9ecef;
}

.td-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.td-header strong {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.85rem;
}

.td-header strong i {
    color: #667eea;
}

.td-boards {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.td-board-item {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    min-height: 350px;
}

.td-remove-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10;
}

.td-empty {
    font-size: 0.8rem;
    color: #6c757d;
    font-style: italic;
    margin: 0;
}
</style>
